import { Box, Card, CardContent, Stack, Typography, InputAdornment, IconButton } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { FiEye, FiEyeOff } from "react-icons/fi";
import LabelInputField from "../../components/core-components/label-input-field/LabelInputField";
import CoreButtons from "../../components/core-components/button/CoreButtons";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { ROUTE_PATHS } from "../../routes";
import { changePasswordThunk } from "../../store/user/userThunk";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../types/coreButtonTypes";
import { useToast } from "../../context/ToasterContext";
import { RESET_PASSWORD_SCHEMA } from "../../utils/yup-schemas/yupSchemas";
import {
	PASSWORD_UPPERCASE_REGEX,
	PASSWORD_DIGIT_REGEX,
	PASSWORD_SPECIAL_CHAR_REGEX,
} from "../../utils/regex";
import palette from "../../theme/palette";
import typography from "../../theme/typography";
import IsMobile from "../../hooks/isMobile";
import { iApiError } from "../../types/commonTypes";
import { useUserContext } from "../../context/UserContext";
import STATUS_CODES from "../../constants/statusCodesConstants";

type ChangePasswordForm = yup.InferType<typeof RESET_PASSWORD_SCHEMA>;

const HINTS = [
	{ key: "minLength", label: "Minimum of 8 characters", test: (v: string) => v.length >= 8 },
	{ key: "uppercase", label: "One uppercase letter", test: (v: string) => PASSWORD_UPPERCASE_REGEX.test(v) },
	{ key: "number", label: "One number", test: (v: string) => PASSWORD_DIGIT_REGEX.test(v) },
	{
		key: "special",
		label: "One special character",
		test: (v: string) => PASSWORD_SPECIAL_CHAR_REGEX.test(v),
	},
];

/**
 * Where the router holds a vendor still on the temporary password from the
 * Published email. The change happens in-session, so the vendor stays logged
 * in; the dashboard, and with it the T&Cs gate, follows.
 */
export default function ChangePasswordPage() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { showToast } = useToast();
	const isMobile = IsMobile();
	const { logout } = useUserContext();

	const temporaryPassword = useAppSelector((state) => state.user.temporaryPassword);
	const [currentPassword, setCurrentPassword] = useState("");
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
	const [touched, setTouched] = useState<{ password?: boolean; confirmPassword?: boolean }>({});
	const [loading, setLoading] = useState(false);

	const validate = (values: ChangePasswordForm): boolean => {
		try {
			RESET_PASSWORD_SCHEMA.validateSync(values, { abortEarly: false });
			setErrors({});
			return true;
		} catch (err) {
			if (yup.ValidationError.isError(err)) {
				const next: Record<string, string> = {};
				err.inner.forEach((e) => {
					if (e.path) next[e.path] = e.message;
				});
				setErrors(next as { password?: string; confirmPassword?: string });
			}
			return false;
		}
	};

	const isValid =
		(!!temporaryPassword || currentPassword.length > 0) &&
		password.length >= 8 &&
		PASSWORD_UPPERCASE_REGEX.test(password) &&
		PASSWORD_DIGIT_REGEX.test(password) &&
		PASSWORD_SPECIAL_CHAR_REGEX.test(password) &&
		confirmPassword === password;

	const handleSubmit = async () => {
		if (!validate({ password, confirmPassword })) return;
		setLoading(true);
		const resp = await dispatch(
			changePasswordThunk({ currentPassword: temporaryPassword || currentPassword, newPassword: password }),
		);
		setLoading(false);

		if (changePasswordThunk.fulfilled.match(resp)) {
			// Changed, but no new session came back: sign in with the new password.
			if (!resp.payload.data) {
				showToast("Password updated. Please sign in with your new password.");
				logout(ROUTE_PATHS.LOGIN);
				return;
			}
			showToast(resp.payload.message || "Password updated successfully.");
			navigate(ROUTE_PATHS.AI_DIRECT_REQUEST, { replace: true });
			return;
		}

		const error = resp.payload as iApiError | undefined;
		showToast(error?.message || "Something went wrong. Please try again.", undefined, "error");
		// Expired temporary password: only Forgot password can recover it.
		if (error?.statusCode === STATUS_CODES.AUTH.TEMPORARY_PASSWORD_EXPIRED) {
			logout(ROUTE_PATHS.LOGIN, () => navigate(ROUTE_PATHS.FORGOT_PASSWORD));
		}
	};

	return (
		<Card sx={{ width: "100%", backgroundColor: "transparent", boxShadow: "none", border: "none" }}>
			<CardContent sx={{ p: 3 }}>
				<Stack spacing={2} sx={{ alignItems: "center", mb: 4 }}>
					<Typography sx={{ textAlign: "center", fontFamily: typography.fontSecondary,
							fontSize: isMobile ? 32 : 38,
							fontWeight: 600,
							color: palette.secondary.main, }}>
						Set new password
					</Typography>
					<Typography
						variant="body1" sx={{ color: palette.grey[950], textAlign: "center", fontFamily: typography.fontPrimaryGeneralSans,
							fontWeight: typography.fontWeightRegular, }}>
						You signed in with a temporary password. Choose a new one to continue.
					</Typography>
				</Stack>
				<Box component="form" noValidate sx={{ maxWidth: 500, width: "100%", mx: "auto" }}>
					{!temporaryPassword && (
						<LabelInputField
							id="currentPassword"
							label="Temporary password"
							type={showCurrentPassword ? "text" : "password"}
							placeholder="Enter the password from your email"
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
							className="form-group"
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle temporary password visibility"
											onClick={() => setShowCurrentPassword((p) => !p)}
											edge="end"
											size="small"
										>
											{showCurrentPassword ? <FiEyeOff /> : <FiEye />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					)}
					<LabelInputField
						id="password"
						label="New password"
						type={showPassword ? "text" : "password"}
						placeholder="Enter new password"
						value={password}
						onChange={(e) => {
							const nextPassword = e.target.value;
							setPassword(nextPassword);
							validate({ password: nextPassword, confirmPassword });
						}}
						onBlur={() => setTouched((t) => ({ ...t, password: true }))}
						error={touched.password && !!errors.password}
						helperText={""}
						className="form-group"
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										onClick={() => setShowPassword((p) => !p)}
										edge="end"
										size="small"
									>
										{showPassword ? <FiEyeOff /> : <FiEye />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<Box component="ul" sx={{ mt: 1, pl: 2.5, mb: 2, listStyle: "disc" }}>
						{HINTS.map((h) => {
							const value = password;
							const satisfied = value.length > 0 && h.test(value);
							const failed = value.length > 0 && !h.test(value);
							const color = failed
								? palette.error.main
								: satisfied
									? palette.success.main
									: palette.grey[700];
							return (
								<Box
									component="li"
									key={h.key}
									sx={{
										fontSize: 16,
										color,
										mb: 0.5,
										fontFamily: typography.fontPrimaryGeneralSans,
										fontWeight: typography.fontWeightRegular,
									}}
								>
									{h.label}
								</Box>
							);
						})}
					</Box>
					<LabelInputField
						id="confirmPassword"
						label="Confirm password"
						type={showConfirmPassword ? "text" : "password"}
						placeholder="Confirm new password"
						value={confirmPassword}
						onChange={(e) => {
							const nextConfirm = e.target.value;
							setConfirmPassword(nextConfirm);
							validate({ password, confirmPassword: nextConfirm });
						}}
						onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
						error={
							(touched.confirmPassword && !!errors.confirmPassword) ||
							(confirmPassword.length > 0 && password !== confirmPassword)
						}
						errorTextOnly
						helperText={
							touched.confirmPassword
								? errors.confirmPassword
								: confirmPassword.length > 0 && password !== confirmPassword
									? "Confirm Password should match with the New Password"
									: undefined
						}
						className="form-group"
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle confirm password visibility"
										onClick={() => setShowConfirmPassword((p) => !p)}
										edge="end"
										size="small"
									>
										{showConfirmPassword ? <FiEyeOff /> : <FiEye />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<Box sx={{ mt: 2 }}>
						<CoreButtons
							buttonText="Submit"
							fullWidth
							coreBtnPill
							variant={BUTTON_VARIANT.CONTAINED}
							color={BUTTON_COLOR.PRIMARY}
							onclick={handleSubmit}
							disabled={!isValid}
							loading={loading}
						/>
					</Box>
				</Box>
			</CardContent>
		</Card>
	);
}
